/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { css } from "@emotion/css";
import type { PageAction, PrimaryPageAction, SimpleMenuItem } from "@octopusdeploy/design-system-components";
import { EmptyStateNoResults, List, ListItem, Pagination, Snackbar } from "@octopusdeploy/design-system-components";
import { arrayValueFromQueryString, DeprecatedSelect } from "@octopusdeploy/design-system-octopus-components";
import { text } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { AuditStreamConfigurationResource, DocumentTypeResource, EnvironmentResource, EventAgentResource, EventCategoryResource, EventGroupResource, ProjectGroupResource, ProjectSummaryResource, Repository, SpaceResource, TenantResource, UserResource, } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { isEqual } from "lodash";
import type { Moment } from "moment";
import moment from "moment";
import type * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { AuditStreamButton } from "~/areas/configuration/components/AuditLayout/AuditStream/AuditStreamButton";
import { isAuditStreamAccessible } from "~/areas/configuration/components/AuditLayout/AuditStream/utils";
import { client, session } from "~/clientInstance";
import { AdvancedFilters } from "~/components/AdvancedFilterLayout/AdvancedFilters";
import { RegardingDocuments } from "~/components/AdvancedFilterLayout/RegardingDocuments";
import AuditListRow from "~/components/AuditListRow/AuditListRow";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import DatePickerDialog from "~/components/Dialog/DatePickerDialog";
import type { EventFilterSelections } from "~/components/EventFilter";
import { EventFilter } from "~/components/EventFilter";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { isLicensedFeatureAvailable } from "~/components/LicensedFeature/LicensedFeatureContext";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import type { IQuery } from "~/components/QueryStringFilters/QueryStringFilters";
import { QueryStringFilters } from "~/components/QueryStringFilters/QueryStringFilters";
import Section from "~/components/Section";
import { defaultContainerKey, expanderActions } from "~/components/form/Sections/reducers/expanders";
import DateFormatter from "~/utils/DateFormatter";
import SystemUserFactory from "~/utils/SystemUserFactory";
import store from "../../../../store";
interface BaseAuditQuery {
    environments: string[];
    users: string[];
    projects: string[];
    projectGroups: string[];
    eventCategories: string[];
    eventGroups: string[];
    eventAgents: string[];
    documentTypes: string[];
    tenants: string[];
    tags: string[];
    from?: string;
    to?: string;
    regardingAny: string[];
    spaces: string[];
}
interface EventListOptions {
    environments?: string[];
    users?: string[];
    projects?: string[];
    projectGroups?: string[];
    eventCategories?: string[];
    eventGroups?: string[];
    eventAgents?: string[];
    documentTypes?: string[];
    tenants?: string[];
    tags?: string[];
    from?: string;
    to?: string;
    regardingAny?: string[];
    spaces?: string[];
    includeSystem?: string;
    asCsv?: boolean;
    excludeDifference?: boolean;
}
interface AuditQuery extends BaseAuditQuery {
    includeSystem?: boolean;
}
// Using the QueryStringFilters requires a type with all values as strings
// This can be removed when switching to managing query params through the routing infrastructure
interface LegacyAuditQuery extends BaseAuditQuery, IQuery {
    includeSystem?: string;
}
interface AuditPageLoaderData {
    environments: EnvironmentResource[];
    users: UserResource[];
    projects: ProjectSummaryResource[];
    projectGroups: ProjectGroupResource[];
    eventCategories: EventCategoryResource[];
    eventGroups: EventGroupResource[];
    eventAgents: EventAgentResource[];
    documentTypes: DocumentTypeResource[];
    tenants: TenantResource[];
    spaces: SpaceResource[];
    auditStreamConfiguration: AuditStreamConfigurationResource | null;
    isTrial: boolean;
}
interface AuditFilter extends EventFilterSelections {
    DateOption: DateOption;
    DateRange: DateRange;
    RegardingAny: string[];
    Spaces: string[];
    IncludeSystem: boolean;
}
export enum DateOption {
    Today = "Today",
    Last7Days = "Last 7 Days",
    Last30Days = "Last 30 Days",
    Last60Days = "Last 60 Days",
    Custom = "Custom"
}
interface DateRange {
    startDate: Moment;
    endDate: Moment;
}
const AUDIT_PAGE_SIZE = 30;
const AuditQueryStringFilters = QueryStringFilters<AuditFilter, LegacyAuditQuery>;
const dateFormatForApi = "YYYY-MM-DDTHH:mm:ss.Z"; // This matches the server-side date format for subscription notification deep links, play carefully!
const dateFormatForHumans = "D MMMM YYYY";
export const auditPageTitle = "Audit";
export async function auditPageLoader(repository: Repository): Promise<AuditPageLoaderData> {
    const environments = isAllowed({ permission: Permission.EnvironmentView, environment: "*" }) ? repository.Environments.all() : Promise.resolve<EnvironmentResource[]>([]);
    const usersPromise = isAllowed({ permission: Permission.UserView }) ? repository.Users.all() : Promise.resolve<UserResource[]>([]);
    const projects = isAllowed({ permission: Permission.ProjectView, wildcard: true }) ? repository.Projects.summaries() : Promise.resolve<ProjectSummaryResource[]>([]);
    const projectGroups = isAllowed({ permission: Permission.ProjectGroupView, projectGroup: "*" }) ? repository.ProjectGroups.all() : Promise.resolve<ProjectGroupResource[]>([]);
    const eventCategories = repository.Events.categories({});
    const eventGroups = repository.Events.groups({});
    const eventAgents = repository.Events.eventAgents();
    const documentTypes = repository.Events.documentTypes({});
    const tenants = isAllowed({ permission: Permission.TenantView, tenant: "*" }) ? repository.Tenants.all() : Promise.resolve([]);
    const spaces = repository.Users.getSpaces(session.currentUser!);
    const auditStreamLicenseAvailable: boolean = isAuditStreamAccessible() ? await isLicensedFeatureAvailable("AuditLogFeeds") : false;
    const users = await usersPromise;
    users.unshift(SystemUserFactory.systemUser());
    const licenseStatusPromise = auditStreamLicenseAvailable ? repository.Licenses.getCurrentStatus() : null;
    const auditStreamConfiguration = auditStreamLicenseAvailable ? await repository.AuditStreamConfiguration.get() : null;
    return {
        environments: await environments,
        users,
        projects: await projects,
        projectGroups: await projectGroups,
        eventCategories: await eventCategories,
        eventGroups: await eventGroups,
        eventAgents: await eventAgents,
        documentTypes: await documentTypes,
        tenants: await tenants,
        spaces: await spaces,
        auditStreamConfiguration,
        isTrial: licenseStatusPromise ? (await licenseStatusPromise).IsTrial : false,
    };
}
interface AuditPageProps {
    loaderData: AuditPageLoaderData;
    queryParams: AuditQuery;
}
export function AuditPage({ loaderData, queryParams }: AuditPageProps) {
    const { doBusyTask, status: legacyDoBusyTaskStatus } = useLegacyDoBusyTask();
    const [pageIndex, setPageIndex] = useState(1);
    const [filterSelections, setFilterSelections] = useState(getInitialFilter(queryParams));
    const [datePickerDialogOpen, setDatePickerDialogOpen] = useState(false);
    const [dateOptions, setDateOptions] = useState(Object.values(DateOption));
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [auditStreamConfiguration, setAuditStreamConfiguration] = useState(loaderData.auditStreamConfiguration);
    const eventListOptions = useMemo(() => getEventListOptions(filterSelections), [filterSelections]);
    const eventQuery = useQuery(async (repository) => {
        if (!isAllowed({ permission: Permission.EventView, wildcard: true })) {
            return null;
        }
        const paginationRequestParams = { skip: (pageIndex - 1) * AUDIT_PAGE_SIZE, take: AUDIT_PAGE_SIZE + 1 };
        if (isFeatureToggleEnabled("PermissionCheckOptimizationFeatureToggle")) {
            const response = await repository.Events.getUnpaginatedEvents(eventListOptions);
            return { Events: response.Items, PageSize: response.TotalResults };
        }
        else {
            const response = await repository.Events.search({ ...eventListOptions, ...paginationRequestParams });
            return { Events: response.Events, PageSize: AUDIT_PAGE_SIZE };
        }
    }, [eventListOptions, pageIndex], "Audit events");
    useEffect(() => {
        return () => {
            store.dispatch(expanderActions.onExpanderContainerDestroyed({ containerKey: defaultContainerKey }));
        };
    }, []);
    const overflowActions = [createSubscriptionAction(), manageArchivesAction()].filter((item) => item !== null);
    const auditStreamPageAction: PageAction = {
        type: "custom",
        key: "Audit Stream",
        content: (<AuditStreamButton onUpdate={(auditStreamConfiguration: AuditStreamConfigurationResource, message: string) => onAuditStreamUpdate(auditStreamConfiguration, message)} auditStreamConfiguration={auditStreamConfiguration} isTrial={loaderData.isTrial}/>),
        hasPermissions: isAllowed({
            permission: Permission.ConfigureServer,
            wildcard: true,
        }),
    };
    const refreshPageAction: PageAction = {
        type: "button",
        buttonType: "secondary",
        label: "Refresh",
        onClick: () => eventQuery.refetch(),
        hasPermissions: isAllowed({
            permission: Permission.EventView,
            wildcard: true,
        }),
    };
    const exportPageAction: PrimaryPageAction = {
        type: "button",
        label: "Export",
        onClick: () => onExportClicked(),
        hasPermissions: isAllowed({
            permission: Permission.EventView,
            wildcard: true,
        }),
    };
    const pageActions = [auditStreamPageAction, refreshPageAction];
    if (!isAllowed({ permission: Permission.EventView, wildcard: true })) {
        return (<PageContent header={{ pageActions, primaryAction: exportPageAction, overflowActions, title: auditPageTitle }} callout={{
                type: "information",
                title: "Permission required",
                content: `The ${Permission.EventView} permission is required to view the audit log`,
            }}>
                {null}
            </PageContent>);
    }
    return (<PageContent legacyStatus={legacyDoBusyTaskStatus} header={{ pageActions, primaryAction: exportPageAction, overflowActions, title: auditPageTitle }} filters={{
            inputs: [dateFilterHeader(dateOptions)],
            advancedFilters: {
                content: advancedFilters(),
                onResetFilter: () => onFiltersChanged(getDefaultFilter()),
                hasUserSelectedValues: !isEqual(getDefaultFilter(), filterSelections),
            },
            filtersSummary: dateFilterNote(),
        }} pagination={isFeatureToggleEnabled("PermissionCheckOptimizationFeatureToggle")
            ? undefined
            : {
                ui: (<Pagination currentPage={pageIndex} itemsPerPageOptions={[]} label="Events" onPageChange={(p) => setPageIndex(p)} selectedItemsPerPage={AUDIT_PAGE_SIZE} onPageSizeChange={(_) => { }} totalResults="unknown" hasMoreResults={(eventQuery.result?.Events.length ?? 0) > AUDIT_PAGE_SIZE}/>),
                placement: "topAndBottom",
            }}>
            <AuditQueryStringFilters filter={filterSelections} getQuery={(filter) => legacyQueryFromFilters(filter, true)} getFilter={getFilterFromQuery} onFilterChange={(filters) => {
            onFiltersChanged(filters);
        }}/>
            {renderContent()}
            <Snackbar open={showSnackbar} autoHideDuration={3500} onClose={() => setShowSnackbar(false)} content={snackbarMessage} textAlign={"center"}/>
        </PageContent>);
    function onAuditStreamUpdate(auditStreamConfiguration: AuditStreamConfigurationResource, message: string) {
        setShowSnackbar(true);
        setSnackbarMessage(message);
        setAuditStreamConfiguration(auditStreamConfiguration);
    }
    function renderContent() {
        if (!eventQuery.result)
            return null;
        const { Events: items, PageSize: pageSize } = eventQuery.result;
        return (<>
                <List includeVerticalPadding empty={<Section>
                            <EmptyStateNoResults />
                        </Section>} rowKey={(item) => item.Id} items={items.slice(0, pageSize)} renderRow={({ item }) => {
                const category = loaderData.eventCategories.find((x) => x.Id === item.Category);
                return (<ListItem>
                                <AuditListRow event={item} categoryName={category ? category.Name : item.Category}/>
                            </ListItem>);
            }}/>
            </>);
    }
    function advancedFilters(): React.ReactNode {
        return (<AdvancedFilters>
                {filterSelections.RegardingAny && filterSelections.RegardingAny.length > 0 && (<RegardingDocuments onClear={() => {
                    onFiltersChanged({ ...filterSelections, RegardingAny: [] });
                }}>
                        Regarding documents:
                        <ul>
                            {filterSelections.RegardingAny.map((documentId) => (<li key={documentId}>
                                    <strong>{documentId}</strong>
                                </li>))}
                        </ul>
                    </RegardingDocuments>)}
                <EventFilter doBusyTask={doBusyTask} documentTypes={loaderData.documentTypes} environments={loaderData.environments} eventCategories={loaderData.eventCategories} eventGroups={loaderData.eventGroups} eventAgents={loaderData.eventAgents} projects={loaderData.projects} projectGroups={loaderData.projectGroups} tenants={loaderData.tenants} users={loaderData.users} spaces={loaderData.spaces} selectedDocumentTypes={filterSelections.DocumentTypes} selectedEnvironments={filterSelections.Environments} selectedEventCategories={filterSelections.EventCategories} selectedEventGroups={filterSelections.EventGroups} selectedEventAgents={filterSelections.EventAgents} selectedProjects={filterSelections.Projects} selectedProjectGroups={filterSelections.ProjectGroups} selectedTenants={filterSelections.Tenants} selectedTenantTags={filterSelections.Tags} selectedUsers={filterSelections.Users} selectedSpaces={filterSelections.Spaces} includeSystem={filterSelections.IncludeSystem} onChangeDocumentTypes={(DocumentTypes) => onFiltersChanged({ ...filterSelections, DocumentTypes })} onChangeEnvironments={(Environments) => onFiltersChanged({ ...filterSelections, Environments })} onChangeEventCategories={(EventCategories) => onFiltersChanged({ ...filterSelections, EventCategories })} onChangeEventGroups={(EventGroups) => onFiltersChanged({ ...filterSelections, EventGroups })} onChangeEventAgents={(EventAgents) => onFiltersChanged({ ...filterSelections, EventAgents })} onChangeProjects={(Projects) => onFiltersChanged({ ...filterSelections, Projects })} onChangeProjectGroups={(ProjectGroups) => onFiltersChanged({ ...filterSelections, ProjectGroups })} onChangeTenants={(Tenants) => onFiltersChanged({ ...filterSelections, Tenants })} onChangeTenantTags={(Tags) => onFiltersChanged({ ...filterSelections, Tags })} onChangeUsers={(Users) => onFiltersChanged({ ...filterSelections, Users })} onChangeSpaces={(Spaces) => onFiltersChanged({ ...filterSelections, Spaces })} onIncludeSystemChange={(IncludeSystem) => onFiltersChanged({ ...filterSelections, IncludeSystem })}/>
            </AdvancedFilters>);
    }
    function dateFilterHeader(dateOptionEnum: DateOption[]) {
        const onDateChange = async (value: string) => {
            const dateOption = dateOptionEnum.find((option) => option.toString() === value);
            if (dateOption) {
                if (dateOption === DateOption.Custom) {
                    openDialog();
                }
                else {
                    await onDatePickerChange(dateOption, convertDateOptionToDateRange(dateOption));
                }
            }
        };
        const dateOptions = dateOptionEnum.map((value) => {
            return {
                text: value,
                value,
            };
        });
        return (<div className={dateRangeFilterContainer}>
                {datePickerDialogOpen && (<DatePickerDialog dialogOpen={true} startDate={filterSelections.DateRange.startDate} endDate={filterSelections.DateRange.endDate} onSaveClick={(range) => onDatePickerChange(DateOption.Custom, range)} onCancelClick={() => clearDialogState()}/>)}
                <DeprecatedSelect label="Select date range" items={dateOptions} value={filterSelections.DateOption} onChange={(value) => {
                if (value)
                    return onDateChange(value);
            }} sortItems={false}/>
            </div>);
    }
    function dateFilterNote() {
        const startDate = filterSelections.DateRange.startDate.clone();
        const endDate = filterSelections.DateRange.endDate.clone();
        const dateRangeNote = DateFormatter.dateToCustomFormat(startDate, dateFormatForHumans) + " - " + DateFormatter.dateToCustomFormat(endDate, dateFormatForHumans);
        return (<p className={dateRangeMessage}>
                Showing results for <span className={dateRangeValue}>{dateRangeNote}</span>
            </p>);
    }
    function openDialog() {
        setDatePickerDialogOpen(true);
    }
    function clearDialogState() {
        setDatePickerDialogOpen(false);
    }
    function onExportClicked() {
        window.location.href = client.resolveLinkTemplate("Events", { ...eventListOptions, asCsv: true } satisfies EventListOptions);
    }
    async function onDatePickerChange(dateOption: DateOption, dateRange: DateRange) {
        const newFilterSelections: AuditFilter = {
            ...filterSelections,
            DateOption: dateOption,
            DateRange: {
                startDate: dateRange.startDate.clone().startOf("day"),
                endDate: dateRange.endDate.clone().endOf("day"),
            },
        };
        clearDialogState();
        onFiltersChanged(newFilterSelections);
    }
    function onFiltersChanged(newFilterSelections: AuditFilter) {
        setFilterSelections(newFilterSelections);
        setPageIndex(1);
    }
    function createSubscriptionAction(): SimpleMenuItem | null {
        const createSub = "Create subscription from this filter";
        if (filterSelections.Spaces.length > 1) {
            return { type: "disabled", label: createSub, disableReason: "Multiple Spaces are selected, a subscription can only be created for a single Space" };
        }
        else if (filterSelections.Spaces.length === 0) {
            return { type: "disabled", label: createSub, disableReason: "A subscription can only be created for your current Space" };
        }
        else if (filterSelections.Spaces.length === 1 && filterSelections.Spaces[0] !== client.spaceId) {
            const otherSpaceName = loaderData.spaces.find((s) => s.Id === filterSelections.Spaces[0])!.Name;
            return { type: "disabled", label: createSub, disableReason: `A subscription can only be created for your current Space, you will need to switch to the '${otherSpaceName}' Space to create it.` };
        }
        if (isAllowed({
            permission: Permission.SubscriptionEdit,
        })) {
            return {
                type: "internal-link",
                label: createSub,
                path: links.createSubscriptionPage.generateUrl(queryFromFilters(filterSelections, false)),
            };
        }
        return null;
    }
    function manageArchivesAction(): SimpleMenuItem | null {
        const manageArchive = "Manage archived audit logs";
        if (isAllowed({ permission: Permission.EventRetentionView })) {
            return {
                type: "internal-link",
                label: manageArchive,
                path: links.auditArchivePage.generateUrl(),
            };
        }
        return null;
    }
    function queryFromFilters(filterSelections: AuditFilter, includeDates: boolean): AuditQuery {
        return {
            users: filterSelections.Users,
            projects: filterSelections.Projects,
            projectGroups: filterSelections.ProjectGroups,
            environments: filterSelections.Environments,
            eventGroups: filterSelections.EventGroups,
            eventCategories: filterSelections.EventCategories,
            eventAgents: filterSelections.EventAgents,
            tenants: filterSelections.Tenants,
            tags: filterSelections.Tags,
            documentTypes: filterSelections.DocumentTypes,
            from: filterSelections.DateRange.startDate && includeDates ? filterSelections.DateRange.startDate.format(dateFormatForApi) : undefined,
            to: filterSelections.DateRange.endDate && includeDates ? filterSelections.DateRange.endDate.format(dateFormatForApi) : undefined,
            regardingAny: filterSelections.RegardingAny,
            includeSystem: filterSelections.IncludeSystem,
            spaces: filterSelections.Spaces,
        };
    }
    function legacyQueryFromFilters(filterSelections: AuditFilter, includeDates: boolean): LegacyAuditQuery {
        const auditQuery = queryFromFilters(filterSelections, includeDates);
        return {
            ...auditQuery,
            includeSystem: auditQuery.includeSystem ? "true" : "false",
        };
    }
    function getFilterFromQuery(query: LegacyAuditQuery): AuditFilter {
        const defaultDateConfig = getDefaultDateConfiguration();
        return {
            Users: arrayValueFromQueryString(query.users),
            Projects: arrayValueFromQueryString(query.projects),
            ProjectGroups: arrayValueFromQueryString(query.projectGroups),
            Environments: arrayValueFromQueryString(query.environments),
            EventGroups: arrayValueFromQueryString(query.eventGroups),
            EventCategories: arrayValueFromQueryString(query.eventCategories),
            EventAgents: arrayValueFromQueryString(query.eventAgents),
            Tenants: arrayValueFromQueryString(query.tenants),
            Tags: arrayValueFromQueryString(query.tags),
            DocumentTypes: arrayValueFromQueryString(query.documentTypes),
            DateOption: filterSelections.DateOption,
            DateRange: {
                startDate: getDateFromQueryStringValue(query.from, defaultDateConfig.range.startDate),
                endDate: getDateFromQueryStringValue(query.to, defaultDateConfig.range.endDate),
            },
            RegardingAny: arrayValueFromQueryString(query.regardingAny),
            Spaces: arrayValueFromQueryString(query.spaces),
            IncludeSystem: query.includeSystem === "true",
        };
    }
    function getDateFromQueryStringValue(dateQueryString: string | undefined, defaultDate: Moment) {
        if (dateQueryString) {
            const parsedDate = moment(dateQueryString, dateFormatForApi);
            if (parsedDate.isSame(defaultDate, "second")) {
                return defaultDate;
            }
            return parsedDate;
        }
        return defaultDate;
    }
    function convertDateOptionToDateRange(option: DateOption): DateRange {
        const startOfToday = moment().startOf("day");
        const endOfToday = moment().endOf("day");
        switch (option) {
            case DateOption.Today:
                return { startDate: startOfToday, endDate: endOfToday };
            case DateOption.Last7Days:
                return { startDate: startOfToday.subtract(7, "days"), endDate: endOfToday };
            case DateOption.Last30Days:
                return { startDate: startOfToday.subtract(30, "days"), endDate: endOfToday };
            case DateOption.Last60Days:
                return { startDate: startOfToday.subtract(60, "days"), endDate: endOfToday };
            default:
                return { startDate: filterSelections.DateRange.startDate.clone(), endDate: filterSelections.DateRange.endDate.clone() };
        }
    }
    // We may not want the dates to be on this object, so that when the user hits the reset button, the dates are unaffected
    // Although on the other hand, you probably want them to 'reset' to the default range
    // to be consistent with the rest of the app... This is the way it is currently implemented
    function getDefaultFilter(): AuditFilter {
        const hasEventViewInCurrentSpace = session.currentPermissions!.scopeToSpace(client.spaceId).hasPermissionInAnyScope(Permission.EventView);
        const shouldFilterToCurrentSpace = client.spaceId && hasEventViewInCurrentSpace;
        const includeSystem = true;
        const defaultDateConfig = getDefaultDateConfiguration();
        return {
            Users: [],
            Projects: [],
            ProjectGroups: [],
            Environments: [],
            EventGroups: [],
            EventCategories: [],
            EventAgents: [],
            Tenants: [],
            Tags: [],
            DocumentTypes: [],
            DateOption: defaultDateConfig.option,
            DateRange: {
                startDate: defaultDateConfig.range.startDate,
                endDate: defaultDateConfig.range.endDate,
            },
            RegardingAny: [],
            Spaces: shouldFilterToCurrentSpace && client.spaceId ? [client.spaceId] : [],
            IncludeSystem: includeSystem,
        };
    }
    function getInitialFilter(queryParams: AuditQuery): AuditFilter {
        const defaultFilter = getDefaultFilter();
        return {
            Users: queryParams.users,
            Projects: queryParams.projects,
            ProjectGroups: queryParams.projectGroups,
            Environments: queryParams.environments,
            EventGroups: queryParams.eventGroups,
            EventCategories: queryParams.eventCategories,
            EventAgents: queryParams.eventAgents,
            Tenants: queryParams.tenants,
            Tags: queryParams.tags,
            DocumentTypes: queryParams.documentTypes,
            DateOption: defaultFilter.DateOption,
            DateRange: {
                startDate: getDateFromQueryStringValue(queryParams.from, defaultFilter.DateRange.startDate),
                endDate: getDateFromQueryStringValue(queryParams.to, defaultFilter.DateRange.endDate),
            },
            RegardingAny: queryParams.regardingAny,
            Spaces: queryParams.spaces.length > 0 ? queryParams.spaces : defaultFilter.Spaces,
            IncludeSystem: queryParams.includeSystem ?? defaultFilter.IncludeSystem,
        };
    }
    function getDefaultDateConfiguration(): {
        option: DateOption;
        range: DateRange;
    } {
        return { option: DateOption.Last30Days, range: convertDateOptionToDateRange(DateOption.Last30Days) };
    }
}
function getEventListOptions(filterSelections: AuditFilter): EventListOptions {
    function isNotEmpty<T>(items: T[]) {
        return items.length !== 0;
    }
    // Recall API.
    const eventListOptions: EventListOptions = {};
    if (isNotEmpty(filterSelections.Users)) {
        eventListOptions.users = filterSelections.Users;
    }
    if (isNotEmpty(filterSelections.Projects)) {
        eventListOptions.projects = filterSelections.Projects;
    }
    if (isNotEmpty(filterSelections.ProjectGroups)) {
        eventListOptions.projectGroups = filterSelections.ProjectGroups;
    }
    if (isNotEmpty(filterSelections.Environments)) {
        eventListOptions.environments = filterSelections.Environments;
    }
    if (isNotEmpty(filterSelections.EventGroups)) {
        eventListOptions.eventGroups = filterSelections.EventGroups;
    }
    if (isNotEmpty(filterSelections.EventCategories)) {
        eventListOptions.eventCategories = filterSelections.EventCategories;
    }
    if (isNotEmpty(filterSelections.EventAgents)) {
        eventListOptions.eventAgents = filterSelections.EventAgents;
    }
    if (isNotEmpty(filterSelections.Tags)) {
        eventListOptions.tags = filterSelections.Tags;
    }
    if (isNotEmpty(filterSelections.Tenants)) {
        eventListOptions.tenants = filterSelections.Tenants;
    }
    if (isNotEmpty(filterSelections.DocumentTypes)) {
        eventListOptions.documentTypes = filterSelections.DocumentTypes;
    }
    if (isNotEmpty(filterSelections.RegardingAny)) {
        eventListOptions.regardingAny = filterSelections.RegardingAny;
    }
    eventListOptions.spaces = getSpacesFilter(filterSelections);
    eventListOptions.includeSystem = filterSelections.IncludeSystem ? "true" : "false";
    eventListOptions.from = filterSelections.DateRange.startDate.format();
    eventListOptions.to = filterSelections.DateRange.endDate.format();
    eventListOptions.excludeDifference = true;
    return eventListOptions;
}
function getSpacesFilter(filterSelections: AuditFilter) {
    const hasEventViewInAnySpace = session.currentPermissions!.hasPermissionInAnyScope(Permission.EventView);
    if (filterSelections.Spaces.length === 0) {
        if (hasEventViewInAnySpace) {
            return ["all"];
        }
        else {
            return [];
        }
    }
    return filterSelections.Spaces;
}
const dateRangeFilterContainer = css({
    width: "16.5rem",
});
const dateRangeMessage = css({
    margin: "0",
});
const dateRangeValue = css({
    font: text.body.bold.medium,
});
